import { Formik } from 'formik';
import React, { ChangeEvent } from 'react';
import * as Yup from 'yup';
import { getCityAndState, IAppointment, ICityAndState } from '../../../API/GoMoto';
import { AccountType, Country } from '../../../API/Enums';
import { useAppointmentUpdateAndPersist, useEnsureAppointment } from '../../../context/Appointment/appointment';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { CTAButton } from '../../../controls/ButtonBase';
import { useLocalization, useLocalizationExtension } from '../../../context/Localization/localization';
import { Heading } from '../../../controls/Heading/Heading';
import { KeyboardLayouts } from '../../../controls/SimpleKeyboard/Layout';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { isElectron } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { getStateList } from '../../../helpers/StateList';
import { nameof } from '../../../helpers/nameof';
import { GMFormAutocomplete } from '../../../controls/Autocomplete';
import { GMFormPostalCodeField, GMFormTextField, GMFormZipCodeField } from '../../../controls/TextField';
import { GMForm, GMFormWrapper } from '../../../controls/Form';

export interface IProps {
    onProceed: () => void;
}
export const InformationForm: React.FC<IProps> = React.memo((props) => {
    const Localization = useLocalization();
    const { formatInvalid, zipCodeInvalid, postalCodeInvalid, formatRequired } = useLocalizationExtension();
    const { appointment, isLoading } = useEnsureAppointment();
    const setAppointment = useAppointmentUpdateAndPersist();
    const {
        dealer: { country },
    } = useKioskState();

    const stateList = getStateList(country);
    const stateOrProvinceLocalized = country === Country.Canada ? Localization.Province : Localization.State;
    const postalCodeLocalized = country === Country.Canada ? Localization.PostalCode : Localization.ZipCode;
    const postalCodeRegex: RegExp = country === Country.Canada ? new RegExp(/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/) : new RegExp(/^\d{5}$/);
    const postalCodeLengthErrorLocalized = country === Country.Canada ? postalCodeInvalid : zipCodeInvalid;
    const initialValues = {
        ...appointment,
        accountType: isElectron() ? appointment?.accountType || AccountType.individual : AccountType.individual,
        businessName: appointment?.businessName || '',
        firstName: appointment?.firstName || '',
        lastName: appointment?.lastName || '',
        street: appointment?.street || '',
        postalCode: appointment?.postalCode || '',
        city: appointment?.city || '',
        // This is a DDL, so we default to null. Adjusted in onSubmit.
        stateOrProvince: appointment?.stateOrProvince || '',
    } as IAppointment;

    const postalCodeRef = React.useRef('');

    const [loadingPostalAutofill, setLoadingPostalAutofill] = React.useState(false);

    const getUSCityState = (value: string, setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void) => {
        postalCodeRef.current = value;
        if (!loadingPostalAutofill) {
            setLoadingPostalAutofill(true);
            getCityAndState(value)
                .then((cityState) => {
                    ((cityState as ICityAndState).stateOrProvince ?? '') !== '' &&
                        setFieldValue(nameof<IAppointment>('stateOrProvince'), (cityState as ICityAndState).stateOrProvince?.toUpperCase());
                    ((cityState as ICityAndState).city ?? '') !== '' && setFieldValue(nameof<IAppointment>('city'), (cityState as ICityAndState).city);
                })
                .finally(() => {
                    setLoadingPostalAutofill(false);
                })
                .catch(() => setLoadingPostalAutofill(false));
        }
    };

    const getCanadaCityState = (value: string, setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void) => {
        postalCodeRef.current = value;
        const newValue = value.replace(/ /g, '');
        if (!loadingPostalAutofill) {
            setLoadingPostalAutofill(true);
            getCityAndState(newValue.toLocaleUpperCase())
                .then((cityState) => {
                    ((cityState as ICityAndState).stateOrProvince ?? '') !== '' &&
                        setFieldValue(nameof<IAppointment>('stateOrProvince'), (cityState as ICityAndState).stateOrProvince?.toUpperCase());
                    ((cityState as ICityAndState).city ?? '') !== '' && setFieldValue(nameof<IAppointment>('city'), (cityState as ICityAndState).city);
                })
                .finally(() => {
                    setLoadingPostalAutofill(false);
                })
                .catch(() => setLoadingPostalAutofill(false));
        }
    };

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values) => {
                /*
                 * the current implementation marks the field and a number
                 * this is in order to bring up the number pad on the phone
                 * here we parse it back to a string to prevent the server from erroring
                 */
                const updatedAppt: IAppointment = {
                    ...appointment!,
                    ...values,
                    stateOrProvince: values.stateOrProvince || '',
                    postalCode: values.postalCode.toString(),
                    customerInfoUpdated:
                        appointment?.customerInfoUpdated ||
                        initialValues.firstName !== values.firstName ||
                        initialValues.lastName !== values.lastName ||
                        initialValues.street !== values.street ||
                        initialValues.postalCode !== values.postalCode.toString() ||
                        initialValues.city !== values.city ||
                        initialValues.stateOrProvince !== values.stateOrProvince,
                };

                return setAppointment(updatedAppt)
                    .then(props.onProceed);
            }}
            validationSchema={Yup.object()
                .shape({
                    [nameof<typeof initialValues>('firstName')]: Yup.string()
                        .when(nameof<typeof initialValues>('accountType'), {
                            is: (value: AccountType) => value === AccountType.individual,
                            then: (schema) => schema.nullable()
                                .required(formatRequired(Localization.FirstName)),
                            otherwise: (schema) => schema.nullable(),
                        }),
                    [nameof<typeof initialValues>('lastName')]: Yup.string()
                        .when(nameof<typeof initialValues>('accountType'), {
                            is: (value: AccountType) => value === AccountType.individual,
                            then: (schema) => schema.required(formatRequired(Localization.LastName))
                                .nullable(),
                            otherwise: (schema) => schema.nullable(),
                        }),
                    [nameof<typeof initialValues>('street')]: Yup.string()
                        .required(formatRequired(Localization.Street))
                        .nullable(),
                    [nameof<typeof initialValues>('postalCode')]: Yup.string()
                        .required(formatRequired(postalCodeLocalized))
                        .matches(postalCodeRegex, postalCodeLengthErrorLocalized)
                        .nullable(),
                    [nameof<typeof initialValues>('city')]: Yup.string()
                        .required(formatRequired(Localization.City))
                        .nullable(),
                    [nameof<typeof initialValues>('stateOrProvince')]: Yup.string()
                        .oneOf(stateList || [], formatInvalid(stateOrProvinceLocalized))
                        .required(formatRequired(stateOrProvinceLocalized))
                        .nullable(),
                })}
        >
            {({ values, dirty, setFieldValue }) => (
                <GMForm
                    heading={
                        <Heading
                            primaryHeading={Localization.LetsGetAcquainted}
                            secondaryHeading={Localization.CanYouEnterYourInformation}
                            leftAlign
                        />
                    }
                    submitButton={
                        <CTAButton
                            styleType={'primary'}
                            type={'submit'}
                            title={Localization.ClickToProceedToNextStep}
                            label={Localization.Proceed}
                            id={Localization.ClickToProceedToNextStep}
                            ariaLabel={Localization.ClickToProceedToNextStep}
                        />
                    }
                    topDivider
                >
                    <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                    <GMFormTextField
                        name={nameof<typeof values>('firstName')}
                        id={nameof<typeof values>('firstName')}
                        title={Localization.FirstName}
                        label={Localization.FirstName}
                        ariaLabel={Localization.FirstName}
                        disabled={loadingPostalAutofill}
                        autoFocus={initialValues.firstName === ''}
                        shiftAfterSpace
                        required
                    />
                    <GMFormTextField
                        name={nameof<typeof values>('lastName')}
                        id={nameof<typeof values>('lastName')}
                        title={Localization.LastName}
                        label={Localization.LastName}
                        ariaLabel={Localization.LastName}
                        disabled={loadingPostalAutofill}
                        shiftAfterSpace
                        required
                    />
                    <GMFormTextField
                        name={nameof<typeof values>('street')}
                        id={nameof<typeof values>('street')}
                        title={Localization.Street}
                        label={Localization.Street}
                        ariaLabel={Localization.Street}
                        disabled={loadingPostalAutofill}
                        keyboardLayout={KeyboardLayouts.AlphanumericNumpad}
                        shiftAfterSpace
                        required
                    />
                    {country === Country.Canada ? (
                        <GMFormPostalCodeField
                            name={nameof<typeof values>('postalCode')}
                            id={nameof<typeof values>('postalCode')}
                            title={postalCodeLocalized}
                            label={postalCodeLocalized}
                            ariaLabel={postalCodeLocalized}
                            keyboardLayout={KeyboardLayouts.AlphanumericNumpad}
                            onChangeCallback={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                if (event.target.value.length === 7 && dirty) {
                                    getCanadaCityState(event.target.value, setFieldValue);
                                }
                            }}
                            capsLock
                            required
                        />
                    ) : (
                        <GMFormZipCodeField
                            name={nameof<typeof values>('postalCode')}
                            id={nameof<typeof values>('postalCode')}
                            title={postalCodeLocalized}
                            label={postalCodeLocalized}
                            ariaLabel={postalCodeLocalized}
                            keyboardLayout={KeyboardLayouts.NumberPad}
                            onChangeCallback={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                if (event.target.value.length === 5 && dirty) {
                                    getUSCityState(event.target.value, setFieldValue);
                                }
                            }}
                            capsLock
                            required
                        />
                    )}
                    <GMFormTextField
                        name={nameof<typeof values>('city')}
                        id={nameof<typeof values>('city')}
                        title={Localization.City}
                        label={Localization.City}
                        ariaLabel={Localization.City}
                        disabled={loadingPostalAutofill}
                        shiftAfterSpace
                        required
                    />
                    <GMFormAutocomplete
                        name={nameof<typeof values>('stateOrProvince')}
                        id={nameof<typeof values>('stateOrProvince')}
                        title={stateOrProvinceLocalized}
                        label={stateOrProvinceLocalized + '\u00A0*'}
                        ariaLabel={stateOrProvinceLocalized}
                        options={stateList ? stateList : [values.stateOrProvince]}
                        capsLock
                    />
                </GMForm>
            )}
        </Formik>
    );
});
